import { type RefObject, useEffect } from 'react';

import { useTilingEngineContext } from './TilingEngineContext';

type Options = {
    readonly id: string;
    readonly nodeRef: RefObject<HTMLElement | null>;
    readonly active: boolean;
};

/**
 * Publishes the node's measured width to the scroll coordinator while
 * `active`, so other tiles' `align: 'end'` scrolls land flush with the
 * reserved edge instead of disappearing underneath it. Re-publishes on
 * resize and clears the reservation when inactive or unmounted.
 */
export const useEndReservation = ({ id, nodeRef, active }: Options): void => {
    const { setEndReservation } = useTilingEngineContext();

    useEffect(() => {
        const node = nodeRef.current;

        if (node === null || !active) {
            setEndReservation(id, null);

            return () => {
                setEndReservation(id, null);
            };
        }

        const publish = (): void => {
            setEndReservation(id, node.getBoundingClientRect().width);
        };

        publish();

        const observer = new ResizeObserver(publish);
        observer.observe(node);

        return () => {
            observer.disconnect();
            setEndReservation(id, null);
        };
    }, [active, id, nodeRef, setEndReservation]);
};
